import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { createPatient } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Info, Loader2, UserPlus } from "lucide-react";
import { toast } from "sonner";

function splitFullName(fullName) {
  const trimmed = (fullName || "").trim();
  if (!trimmed) return { nombres: "", apellidos: "" };
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return { nombres: parts[0], apellidos: "" };
  return { nombres: parts[0], apellidos: parts.slice(1).join(" ") };
}

function birthDateFromAge(age) {
  const years = parseInt(String(age).trim(), 10);
  if (Number.isNaN(years) || years < 0 || years > 120) return "";
  const year = new Date().getFullYear() - years;
  return `${year}-06-15`;
}

export default function AulaNuevaPractica() {
  const navigate = useNavigate();
  const [nombreCompleto, setNombreCompleto] = useState("");
  const [edad, setEdad] = useState("");
  const [diagnosticoPrincipal, setDiagnosticoPrincipal] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const nombre = nombreCompleto.trim();
    const diagnostico = diagnosticoPrincipal.trim();
    const fechaNacimiento = birthDateFromAge(edad);

    if (!nombre) {
      toast.error("Ingresa un nombre para el paciente ficticio");
      return;
    }
    if (!fechaNacimiento) {
      toast.error("Ingresa una edad válida (0–120 años)");
      return;
    }
    if (!diagnostico) {
      toast.error("Ingresa un diagnóstico principal");
      return;
    }

    const { nombres, apellidos } = splitFullName(nombre);

    setSaving(true);
    try {
      const res = await createPatient({
        nombres,
        apellidos,
        fecha_nacimiento: fechaNacimiento,
        diagnostico_principal: diagnostico,
        alergias: [],
        chronic_conditions: [],
        medicamentos_actuales: [],
        context: "academic",
      });

      const created = res?.data ?? res;
      const patientId = created?.id || created?._id;
      if (!patientId) {
        toast.error("No se recibió el ID del paciente creado");
        return;
      }

      toast.success("Paciente ficticio creado. Iniciando VGI...");
      navigate(`/nueva-consulta?patient_id=${patientId}&modo=aula`);
    } catch {
      toast.error("No se pudo crear el paciente ficticio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6 animate-fade-in">
      <header className="space-y-2">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
          Nueva práctica
        </h1>
        <p className="text-base text-slate-600">
          Crea un paciente ficticio para practicar una VGI completa
        </p>
      </header>

      <div className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3.5 text-sm text-amber-950">
        <Info className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" aria-hidden />
        <p className="leading-relaxed">
          Puedes usar un nombre ficticio. Este paciente no forma parte de ningún expediente clínico real.
        </p>
      </div>

      <Card className="border-violet-200/80 bg-white shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg text-slate-900">
            <UserPlus className="h-5 w-5 text-violet-600" />
            Paciente ficticio
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="nombre-completo">Nombre completo</Label>
              <Input
                id="nombre-completo"
                value={nombreCompleto}
                onChange={(e) => setNombreCompleto(e.target.value)}
                placeholder="Ej. Ana Lucía Morales"
                className="border-violet-200 focus-visible:ring-violet-500"
                disabled={saving}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edad">Edad</Label>
              <Input
                id="edad"
                type="number"
                min={0}
                max={120}
                value={edad}
                onChange={(e) => setEdad(e.target.value)}
                placeholder="Ej. 78"
                className="border-violet-200 focus-visible:ring-violet-500"
                disabled={saving}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="diagnostico">Diagnóstico principal</Label>
              <Input
                id="diagnostico"
                value={diagnosticoPrincipal}
                onChange={(e) => setDiagnosticoPrincipal(e.target.value)}
                placeholder="Ej. Síndrome de fragilidad"
                className="border-violet-200 focus-visible:ring-violet-500"
                disabled={saving}
              />
            </div>

            <Button
              type="submit"
              className="w-full rounded-xl bg-violet-600 text-white hover:bg-violet-700"
              disabled={saving}
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creando paciente...
                </>
              ) : (
                "Crear paciente e iniciar VGI"
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
